import { CancelAction, SubmitAction } from 'components/ListVehicles/components/CreateVehicleDialog/ui';
import styled from 'styled-components';
import { useVehicleUploadTabsContext } from '../context';
import { useField, useForm, useFormState } from 'react-final-form';
import { useGetVehicleByIdQuery, useUpdateVehicleFieldMutation } from 'services/api/upload-vehicle';
import { useDispatch } from 'react-redux';
import { useMutationStatusToast } from 'hooks';
import { useCurrentUser } from 'hooks/selectors';
import { FormApi } from 'final-form';
import { useEffect, useState } from 'react';
import { VEHICLE_FORM_FIELDS, VIN_FORM_FIELDS } from '../constants';

const Wrapper = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  flex: 0 0 auto;

  & > button {
    flex: 1 1 100%;
  }
`;

export const VehicleActions = () => {
  const { selectedTab } = useVehicleUploadTabsContext();

  return (
    <Wrapper>
      <CancelButton />
      {selectedTab === 'vin_info' ? <VinSaveButton /> : <VehicleSaveButton />}
    </Wrapper>
  );
};

const getInitialTabValues = (form: FormApi<any>, fields: string[]) => {
  const formValues = form.getState().values;
  const values: any = {};
  for (const field of fields) {
    values[field] = formValues[field];
  }
  return values;
};

const CancelButton = () => {
  const { selectedTab, setSelectedTab } = useVehicleUploadTabsContext();

  const form = useForm();
  const [initialValues, setInitialValues] = useState<any>();

  useEffect(() => {
    const fields = selectedTab === 'vin_info' ? VIN_FORM_FIELDS : VEHICLE_FORM_FIELDS;
    setInitialValues(getInitialTabValues(form, fields));
  }, [selectedTab]);

  const handleCancel = () => {
    const values = form.getState().values;
    form.initialize({ ...values, ...initialValues });
    setSelectedTab(null);
  };

  return (
    <CancelAction onClick={handleCancel} type="button">
      Cancel
    </CancelAction>
  );
};

const isTabDirty = (dirtyFields: Record<string, boolean>, fields: string[]) => {
  return fields.some(field => !!dirtyFields[field]);
};

const tabHasError = (errorFields: any, fields: string[]) => {
  if (!errorFields) return false;
  return fields.some(field => !!errorFields[field]);
};

const VinSaveButton = () => {
  const form = useForm();

  const {
    input: { value: vehicleId }
  } = useField('vehicleId');

  const { refetch } = useGetVehicleByIdQuery(Number(vehicleId));
  const { setSelectedTab } = useVehicleUploadTabsContext();

  const { meta: vin } = useField('vin', { subscription: { error: true } });
  const { meta: sellerId, input: sellerInput } = useField('seller_id', { subscription: { value: true } });
  const { meta: auctionType } = useField('auction_type', { subscription: { error: true } });

  const user = useCurrentUser();

  const state = useFormState({ subscription: { dirtyFields: true } });
  const isDirty = isTabDirty(state.dirtyFields, VIN_FORM_FIELDS);

  const hasSellerValue = user.role !== 'admin' || (sellerInput.value && sellerInput.value?.value);
  const submitDisabled = vin.error || sellerId.error || auctionType.error || !hasSellerValue;

  const [updateVehicle] = useUpdateVehicleFieldMutation();

  const toast = useMutationStatusToast({
    success: 'Information Saved',
    loading: 'Loading...',
    error: 'Failed to save vehicle'
  });

  const resetTrimRelatedFields = () => {
    form.batch(() => {
      form.change('trim', null);
      form.change('transmission', null);
      form.change('engine', null);
      form.change('drive_train', null);
      form.change('ext_color', null);
    });
  };

  const handleSave = async () => {
    if (submitDisabled) return;
    const values = form.getState().values;
    const vinField = form.getFieldState('vin');
    const { vehicleId, seller_id, vin, auction_type } = values;
    if (!vehicleId) return;
    const promise = updateVehicle({
      id: vehicleId,
      payload: {
        seller_id: seller_id?.value,
        vin,
        auction_type: auction_type?.value
      }
    });

    toast(promise);

    const response = await promise;
    if (!('error' in response)) {
      const responseData = response.data.data;

      if (values.upload_type !== responseData.upload_type || responseData.trim === '') {
        console.log('log trim reset');
        resetTrimRelatedFields();
        form.change('upload_type', responseData.upload_type);
        form.change('year', responseData.year);
        form.change('make', responseData.make);
        form.change('model', responseData.model);
      }
      form.initialize({ ...form.getState().values });

      if (vinField?.dirty) {
        setSelectedTab('vehicle_info');
      } else {
        setSelectedTab(null);
      }
    }
  };

  return (
    <SubmitAction disabled={submitDisabled || !isDirty} onClick={handleSave} type="button">
      Save
    </SubmitAction>
  );
};

const VehicleSaveButton = () => {
  const form = useForm();

  const {
    input: { value: vehicleId }
  } = useField('vehicleId');

  const { refetch } = useGetVehicleByIdQuery(Number(vehicleId));
  const { setSelectedTab } = useVehicleUploadTabsContext();

  const state = useFormState({ subscription: { dirtyFields: true, errors: true } });
  const isDirty = isTabDirty(state.dirtyFields, VEHICLE_FORM_FIELDS);
  const hasError = tabHasError(state.errors, VEHICLE_FORM_FIELDS);

  console.log(state.errors, 'state.errors');

  const submitDisabled = hasError;

  console.log(isDirty, 'isDirty333');
  console.log(submitDisabled, 'submitDisabled');

  const [updateVehicle] = useUpdateVehicleFieldMutation();

  const toast = useMutationStatusToast({
    success: 'Information Saved',
    loading: 'Loading...',
    error: 'Failed to save vehicle'
  });

  const handleSave = async () => {
    if (submitDisabled) return;
    const values = form.getState().values;
    const { vehicleId } = values;
    if (!vehicleId) return;

    const isManual = values.upload_type === 'manual';

    const parseValueField = (field: string) => {
      if (isManual) return values[field];
      return values[field]?.value;
    };

    const promise = updateVehicle({
      id: vehicleId!,
      payload: {
        drive_train: parseValueField('drive_train'),
        engine: parseValueField('engine'),
        ext_color: parseValueField('ext_color'),
        make: values.make,
        model: values.model,
        mileage: values.mileage,
        options: values.options,
        transmission: parseValueField('transmission'),
        trim: parseValueField('trim'),
        year: values.year,
        reserve_price: values.reserve_price,
        starting_bid: values.starting_bid,
        bluebook_mileage: values.bluebook_mileage ? Number(values.bluebook_mileage) : undefined,
        bluebook_value: values.bluebook_value ? Number(values.bluebook_value) : undefined,
        bluebook_mileage_adjustment: values.bluebook_mileage_adjustment
          ? Number(values.bluebook_mileage_adjustment)
          : undefined
      }
    });

    toast(promise);

    const response = await promise;
    if (!('error' in response)) {
      setSelectedTab(null);
      form.initialize({ ...form.getState().values });
    }
  };

  return (
    <SubmitAction disabled={submitDisabled || !isDirty} onClick={handleSave} type="button">
      Save
    </SubmitAction>
  );
};
